import type { ReactNode } from 'react';
import { Button } from '@forecastify/ui';
import { useT, type MessageKey } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A SCREEN NEVER SITS ON "LOADING" FOREVER.                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * This exists because it happened, twice, in one afternoon. A route wrapper written as
 *
 *     const response = await session.request('/account');
 *     if (!response.ok) return;              // ← and the screen stays null
 *
 * turns **every** refusal into a spinner: a 401, a 404 from a missing workspace header, a 500, a
 * contract mismatch. The person sees "Betöltés…" and nothing else, forever, and there is no way to
 * tell from the screen whether the server said no or the request never happened.
 *
 * `state-rules.md` settles the shape once — loading, error, denied, empty — and this is the piece a
 * data-fetching route needs so it cannot accidentally implement only the first.
 *
 * ⚠️ **The failure carries the CODE, and the message is chosen from it** (rule 5: clients branch on
 * `code`, never on `message`). An unrecognised code still renders something, because a screen that
 * says nothing for an unmapped code is the original bug wearing different clothes.
 */

export type ScreenFailure =
  | { readonly kind: 'error'; readonly code: string; readonly requestId?: string | undefined }
  | { readonly kind: 'denied' }
  | { readonly kind: 'no-workspace' };

const CODE: Readonly<Record<string, MessageKey>> = {
  not_found: 'error.not_found',
  permission_denied: 'error.permission_denied',
  conflict: 'error.conflict',
  validation_failed: 'error.validation_failed',
  unauthenticated: 'error.unauthenticated',
};

export function ScreenState({
  failure,
  onRetry,
  children,
}: {
  /** `null` while there is nothing wrong — the screen renders normally. */
  readonly failure: ScreenFailure | null;
  readonly onRetry?: () => void;
  readonly children: ReactNode;
}) {
  const t = useT();

  if (failure === null) return <>{children}</>;

  if (failure.kind === 'no-workspace') {
    /**
     * ⚠️ **Its own state, not an error.** Somebody with an account and no membership is a real
     * situation on the first day — and locally, it is what a wiped development seed looks like.
     * Rendering it as a failure would send them to support over something that is simply true.
     */
    return (
      <div data-screen-state="no-workspace" className="mx-auto max-w-[520px] p-8">
        <h2 className="text-[14px] font-medium">{t('state.noWorkspace.title')}</h2>
        <p className="text-muted-foreground mt-1 text-[12.5px] leading-relaxed">
          {t('state.noWorkspace.body')}
        </p>
      </div>
    );
  }

  if (failure.kind === 'denied') {
    return (
      <div data-screen-state="denied" className="mx-auto max-w-[520px] p-8">
        <h2 className="text-[14px] font-medium">{t('state.denied.title')}</h2>
        <p className="text-muted-foreground mt-1 text-[12.5px]">{t('state.denied.body')}</p>
      </div>
    );
  }

  return (
    <div data-screen-state="error" role="alert" className="mx-auto max-w-[520px] p-8">
      <h2 className="text-[14px] font-medium">{t(CODE[failure.code] ?? 'error.unknown')}</h2>

      {/* ⚠️ The request id, when there is one. A support conversation that starts from a string
          beats one that starts from "it did not work". */}
      {failure.requestId !== undefined && (
        <p className="text-muted-foreground mt-1 text-[11.5px]">
          {t('error.requestId', { id: failure.requestId })}
        </p>
      )}

      {onRetry !== undefined && (
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          {t('state.retry')}
        </Button>
      )}
    </div>
  );
}

/**
 * Turn a refused `Response` into a failure, reading the envelope.
 *
 * ⚠️ **Every non-2xx has the same envelope** (`{ error: { code, message, request_id } }`), so this
 * is the one place that has to know its shape — and a response that is somehow not in that shape
 * still produces a failure rather than `null`, because "the server answered something unexpected"
 * must not render as success.
 */
export async function failureOf(response: Response): Promise<ScreenFailure> {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: unknown; request_id?: unknown };
  } | null;

  const code = typeof body?.error?.code === 'string' ? body.error.code : 'unknown';
  const requestId = typeof body?.error?.request_id === 'string' ? body.error.request_id : undefined;

  if (code === 'permission_denied') return { kind: 'denied' };
  return { kind: 'error', code, requestId };
}
